import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { fetchConversationQueryOptions } from "@/api/assistant";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatMessages } from "@/components/chat/ChatMessages";
import { useChat } from "@/components/chat/useChat";
import { rememberAssistantConversation } from "@/lib/lastAssistantConversation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/assistant/$conversationId",
)({
	loader: async ({ context, params }) => {
		try {
			await context.queryClient.ensureQueryData(
				fetchConversationQueryOptions(params.orgId, params.conversationId),
			);
		} catch {
			throw redirect({
				to: "/app/org/$orgId/assistant",
				params: { orgId: params.orgId },
			});
		}
	},
	component: ConversationPage,
});

function ConversationPage() {
	const { orgId, conversationId } = Route.useParams();

	useEffect(() => {
		rememberAssistantConversation(orgId, conversationId);
	}, [orgId, conversationId]);

	// Conversation is already in cache from the route loader — no extra fetch.
	const { data: conversation } = useSuspenseQuery(
		fetchConversationQueryOptions(orgId, conversationId),
	);

	const { messages, isStreaming, error, activeAgent, sendMessage, retryLast } =
		useChat({
			orgId,
			conversationId,
			establishmentId: conversation.establishmentId ?? undefined,
		});

	return (
		<div className="flex-1 flex flex-col h-full overflow-hidden">
			<ChatMessages
				orgId={orgId}
				conversationId={conversationId}
				messages={messages}
				activeAgent={activeAgent}
				isStreaming={isStreaming}
				onRetry={retryLast}
			/>

			{error && (
				<div className="px-4 pb-1">
					<div className="text-error text-xs">{error}</div>
				</div>
			)}

			<div className="border-t border-base-300 bg-base-100">
				<div className="max-w-3xl mx-auto w-full">
					<ChatInput
						orgId={orgId}
						conversationId={conversationId}
						onSend={sendMessage}
						disabled={isStreaming}
					/>
				</div>
			</div>
		</div>
	);
}
